import { type Specification } from '../core/api';
import { getLayer } from '../layer';

/**
 *
 * @example old spec
 * ```json
 * {
 *  metadata: {},
 *  data: { a: 1, b:2 }[];
 *  x: '',
 *  y: '',
 *  z: '',
 *  stocks: []
 * }
 * ```
 */
// eslint-disable-next-line max-lines-per-function
export function convertLegacySpec(spec: Specification) {
  if (!(spec as Specification & { legacy?: boolean }).legacy) {
    return spec;
  }

  const converted = {
    hook: spec.hook,
    token: spec.token || (spec as unknown as { theme: unknown }).theme,
    layout: spec.layout
  } as Specification;

  converted.data = [
    {
      values: spec.data as unknown as Specification['data'][number]['values'],
      metadata: (spec as unknown as { metadata: unknown }).metadata
    }
  ];

  // old -> new
  /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any */
  const map: { [k: string]: Specification['view']['main']['layers'] } = {
    bar: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute
        }
      }
    ],
    line: [
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute
        }
      }
    ],
    multipleLine: [
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute,
          z: (spec as any).groupAttribute
        }
      }
    ],
    groupedLine: [
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute,
          z: (spec as any).groupAttribute
        }
      }
    ],
    dynamicLine: [
      {
        type: 'dynamicLine',
        encoding: {
          x: (spec as any).xAttribute,
          y: (spec as any).yAttribute
        }
      }
    ],
    stackedLine: [
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute,
          z: (spec as any).legendAttribute
        },
        dvStack: true
      }
    ],
    rankingLine: [
      {
        type: 'rankingLine',
        encoding: {
          x: (spec as any).xAttribute,
          y: (spec as any).yAttribute,
          z: (spec as any).groupAttribute
        }
      }
    ],
    dynamicRankingLine: [
      {
        type: 'dynamicRankingLine',
        encoding: {
          x: (spec as any).xAttribute,
          y: (spec as any).yAttribute,
          z: (spec as any).groupAttribute
        }
      }
    ],
    multiIndicatorStackedLine: [
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any)?.yAxisAttribute || (spec as any)?.groupAttributeArray
        }
      }
    ],
    bandedIntervalTrendLine: [
      {
        type: 'bandedIntervalTrendLine',
        encoding: {
          x: (spec as any).dateAttribute,
          y: (spec as any).valueAttribute,
          y2: (spec as any).upAttribute,
          y3: (spec as any).lowAttribute
        }
      }
    ],
    area: [
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute
        },
        areaStyle: {}
      }
    ],
    stackedArea: [
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute,
          z: (spec as any).legendAttribute
        },
        dvStack: true,
        areaStyle: {}
      }
    ],
    positiveAndNegativeDoubleIndicatorComparisonLine: [
      {
        type: 'positiveAndNegativeDoubleIndicatorComparisonLine',
        encoding: {
          x: (spec as any).dateAttribute,
          y: (spec as any).positiveAttribute,
          y2: (spec as any).negativeAttribute
        }
      }
    ],
    numericText: [
      {
        type: 'summaryText',
        encoding: {
          x: (spec as any).valueAttribute
        }
      }
    ],
    table: [
      {
        type: 'table',
        encoding: {
          x: (spec as any).columnsAttribute
        }
      }
    ],
    verticalTable: [
      {
        type: 'verticalTable',
        encoding: {
          x: (spec as any).columnsAttribute
        }
      }
    ],
    summaryText: [
      {
        type: 'summaryText',
        encoding: {
          x: (spec as any).valueAttribute
        }
      }
    ],
    timeList: [
      {
        type: 'timeList',
        encoding: {
          time: (spec as any).valueAttribute,
          title: (spec as any).titlesAttribute,
          label: (spec as any).labelsAttribute,
          url: (spec as any).urlsAttribute
        }
      }
    ],
    gauge: [
      {
        type: 'gauge',
        encoding: {
          x: (spec as any).gaugeAttribute
        }
      }
    ],
    radar: [
      {
        type: 'radar',
        encoding: {
          x: (spec as any).nameAttribute,
          y: (spec as any).indicatorAttributeArray
        }
      }
    ],
    treeMap: [
      {
        type: 'treeMap',
        encoding: {
          x: (spec as any).nameAttribute,
          y: (spec as any).valueAttribute
        }
      }
    ],
    dynamicTreeMap: [
      {
        type: 'dynamicTreeMap',
        encoding: {
          x: (spec as any).nameAttribute,
          y: (spec as any).valueAttribute,
          group: (spec as any).groupAttribute
        }
      }
    ],
    hxKLine: [
      {
        type: 'hxKLine',
        stocks: (spec as any).stocks
      }
    ],
    hxKLineAndMultiIndicatorLine: [
      {
        type: 'hxKLineAndMultiIndicatorLine',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute
        },
        stocks: (spec as any).stocks
      }
    ],
    hxKLineAndBar: [
      {
        type: 'hxKLineAndBar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute
        },
        stocks: (spec as any).stocks
      }
    ],
    hxKLineAndDynamicLine: [
      {
        type: 'hxKLineAndDynamicLine',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute
        },
        stocks: (spec as any).stocks
      }
    ],
    bar3D: [
      {
        type: 'bar3D',
        encoding: {
          x: (spec as any).xAxis3DAttribute,
          y: (spec as any).yAxis3DAttribute,
          z: (spec as any).zAxis3DAttribute
        }
      }
    ],
    scatter3D: [
      {
        type: 'scatter3D',
        encoding: {
          x: (spec as any).xAxis3DAttribute,
          y: (spec as any).yAxis3DAttribute,
          z: (spec as any).zAxis3DAttribute
        }
      }
    ],
    multiDimensionalScatter3D: [
      {
        type: 'scatter3D',
        encoding: {
          x: (spec as any).xAxis3DAttribute,
          y: (spec as any).yAxis3DAttribute,
          z: (spec as any).zAxis3DAttribute,
          size: (spec as any).size3DAttribute,
          color: (spec as any).color3DAttribute
        }
      }
    ],
    stackedBar: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute,
          z: (spec as any).legendAttribute
        },
        dvStack: true
      }
    ],
    groupedBar: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute,
          z: (spec as any).groupAttribute
        }
      }
    ],
    groupedBarMultiLine: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).barYAxisAttribute,
          z: (spec as any).barGroupAttribute
        }
      },
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).lineYAxisAttribute,
          z: (spec as any).lineGroupAttribute
        }
      }
    ],
    proportionalBar: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute
        },
        showBackground: true
      }
    ],
    groupedStackedBar: [
      {
        type: 'groupedStackedBar',
        encoding: {
          x: (spec as any).nameAttribute,
          y: (spec as any).valueAttribute,
          group: (spec as any).group1Attribute,
          group2: (spec as any).group2Attribute
        }
      }
    ],
    multiIndicatorStackedBar: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any)?.yAxisAttribute || (spec as any)?.groupAttributeArray
        },
        dvStack: true
      }
    ],
    waterfall: [
      {
        type: 'waterfall',
        encoding: {
          x: (spec as any).nameAttribute,
          y: (spec as any).valueAttribute,
          y2: (spec as any).groupAttribute
        }
      }
    ],
    barLine: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).barAxisAttribute
        }
      },
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).lineAxisAttribute
        }
      }
    ],
    doubleIndicatorBarAndLine: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).barAxisAttribute
        }
      },
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).lineAxisAttribute
        }
      }
    ],
    barMultiLine: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).barYAxisAttribute
        }
      },
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).lineYAxisAttribute,
          z: (spec as any).lineZAxisAttribute
        }
      }
    ],
    groupedHorizontalBar: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).yAxisAttribute,
          y: (spec as any).xAxisAttribute,
          z: (spec as any).groupAttribute
        },
        dvHorizontal: true
      }
    ],
    stackedHorizontalBar: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).yAxisAttribute,
          y: (spec as any).xAxisAttribute,
          z: (spec as any).legendAttribute
        },
        dvHorizontal: true,
        dvStack: true
      }
    ],
    rankingHorizontalBar: [
      {
        type: 'rankingHorizontalBar',
        encoding: {
          x: (spec as any).xAttribute,
          y: (spec as any).yAttribute
        }
      }
    ],
    dynamicRankingHorizontalBar: [
      {
        type: 'dynamicRankingHorizontalBar',
        encoding: {
          x: (spec as any).xAttribute,
          y: (spec as any).yAttribute,
          group: (spec as any).groupAttribute
        }
      }
    ],
    dynamicRankingTwoWayHorizontalBar: [
      {
        type: 'dynamicRankingTwoWayHorizontalBar',
        encoding: {
          x: (spec as any).leftxAttribute,
          y: (spec as any).yAttribute,
          x2: (spec as any).rightxAttribute,
          group: (spec as any).groupAttribute
        }
      }
    ],
    rankingTwoWayHorizontalBar: [
      {
        type: 'rankingTwoWayHorizontalBar',
        encoding: {
          x: (spec as any).leftxAttribute,
          y: (spec as any).yAttribute,
          x2: (spec as any).rightxAttribute
        }
      }
    ],
    groupedStackedBarLine: [
      {
        type: 'bar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).barYAxisAttribute
        }
      },
      {
        type: 'line',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).lineYAxisAttribute
        }
      },
      {
        type: 'bar',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).barYAxisAttribute,
          z: (spec as any).groupAttribute
        },
        dvStack: true
      }
    ],
    positiveAndNegativeDoubleIndicatorSumBar: [
      {
        type: 'positiveAndNegativeDoubleIndicatorSumBar',
        encoding: {
          x: (spec as any)?.dateAttribute || (spec as any)?.nameAttribute,
          y: (spec as any).positiveAttribute,
          y2: (spec as any).negativeAttribute
        }
      }
    ],
    donutPie: [
      {
        type: 'pie',
        encoding: {
          x: (spec as any).xAttribute,
          y: (spec as any).yAttribute
        },
        radius: ['50%', '95%']
      }
    ],
    doublePie: [
      {
        type: 'doublePie',
        encoding: {
          x: (spec as any).firstAttribute,
          y: (spec as any).firstValue,
          x2: (spec as any).secondAttribute,
          y2: (spec as any).secondValue
        }
      }
    ],
    radiusPie: [
      {
        type: 'pie',
        encoding: {
          x: (spec as any).nameAttribute,
          y: (spec as any).valueAttribute
        },
        roseType: 'area'
      }
    ],
    polarBar: [
      {
        type: 'polarBar',
        encoding: {
          x: (spec as any).radiusAxis,
          y: (spec as any).valueAttribute
        }
      }
    ],
    pie: [
      {
        type: 'pie',
        encoding: {
          x: (spec as any).xAttribute,
          y: (spec as any).yAttribute
        }
      }
    ],
    dynamicPie: [
      {
        type: 'dynamicPie',
        encoding: {
          x: (spec as any).xAttribute,
          y: (spec as any).yAttribute,
          date: (spec as any).dateAttribute
        }
      }
    ],
    dynamicDonutPie: [
      {
        type: 'dynamicPie',
        encoding: {
          x: (spec as any).xAttribute,
          y: (spec as any).yAttribute,
          date: (spec as any).dateAttribute
        },
        radius: ['50%', '95%']
      }
    ],
    dynamicRadiusPie: [
      {
        type: 'dynamicPie',
        encoding: {
          x: (spec as any).xAttribute,
          y: (spec as any).yAttribute,
          date: (spec as any).dateAttribute
        },
        roseType: 'rouse'
      }
    ],
    dynamicVoronoi: [
      {
        type: 'dynamicVoronoi',
        encoding: {
          x: (spec as any).nameAttribute,
          y: (spec as any).valueAttribute,
          group: (spec as any).groupAttribute
        }
      }
    ],
    multiIndicatorRingGauge: [
      {
        type: 'multiIndicatorRingGauge',
        encoding: {
          x: (spec as any).gaugeAttribute
        }
      }
    ],
    scatter: [
      {
        type: 'scatter',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute
        }
      }
    ],
    multiDimensionalScatter: [
      {
        type: 'scatter',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute,
          size: (spec as any).sizeAttribute,
          color: (spec as any).colorAttribute
        }
      }
    ],
    dynamicBubble: [
      {
        type: 'dynamicBubble',
        encoding: {
          name: (spec as any).nameAttribute,
          value: (spec as any).valuesArray,
          group: (spec as any).groupAttribute
        }
      }
    ],
    dynamicBeeSwarm: [
      {
        type: 'dynamicBeeSwarm',
        encoding: {
          x: (spec as any).xAttribute,
          y: (spec as any).valuesArray,
          y2: (spec as any).groupAttribute
        }
      }
    ],
    timeLine: [
      {
        type: 'timeLine',
        encoding: {
          label: (spec as any).label,
          time: (spec as any).time,
          title: (spec as any).text,
          url: (spec as any)?.url,
          importance: (spec as any)?.importance
        },
        maxHeight: (spec as any).maxHeight,
        width: (spec as any).width,
        customEvent: (spec as any).customEvent
      }
    ],
    text: [
      {
        type: 'text',
        encoding: {
          text: (spec as any).text,
          title: (spec as any).textTitle
        },
        maxHeight: (spec as any).maxHeight,
        width: (spec as any).width,
        customEvent: (spec as any).customEvent
      }
    ],
    columnTable: [
      {
        type: 'multiTable',
        encoding: {
          columns: (spec as any).columns,
          rows: (spec as any).rows
        },
        maxHeight: (spec as any).maxHeight,
        width: (spec as any).width,
        customEvent: (spec as any).customEvent,
        pageSize: (spec as any).pageSize,
        currentPage: (spec as any).currentPage
      }
    ],
    horizontalTable: [
      {
        type: 'multiTable',
        encoding: {
          columns: (spec as any).columns,
          rows: (spec as any).rows
        },
        columnsNum: (spec as any).columnsNum,
        maxHeight: (spec as any).maxHeight,
        width: (spec as any).width,
        customEvent: (spec as any).customEvent
      }
    ],
    multiTableX: [
      {
        type: 'multiTable',
        encoding: {
          columns: (spec as any).columns,
          rows: (spec as any).rows
        },
        maxHeight: (spec as any).maxHeight,
        width: (spec as any).width,
        customEvent: (spec as any).customEvent,
        pageSize: (spec as any).pageSize,
        currentPage: (spec as any).currentPage
      }
    ],
    multiTableY: [
      {
        type: 'multiTable',
        encoding: {
          columns: (spec as any).columns,
          rows: (spec as any).rows
        },
        maxHeight: (spec as any).maxHeight,
        width: (spec as any).width,
        customEvent: (spec as any).customEvent,
        pageSize: (spec as any).pageSize,
        currentPage: (spec as any).currentPage
      }
    ],
    treeTable: [
      {
        type: 'multiTable',
        encoding: {
          columns: (spec as any).columns,
          rows: (spec as any).rows
        },
        isTreeTable: (spec as any)?.isTreeTable,
        maxHeight: (spec as any).maxHeight,
        width: (spec as any).width,
        customEvent: (spec as any).customEvent,
        pageSize: (spec as any).pageSize,
        currentPage: (spec as any).currentPage
      }
    ],
    wordCloud: [
      {
        type: 'wordCloud',
        encoding: {
          x: (spec as any).nameAttribute,
          y: (spec as any).weightAttribute
        },
        colors: (spec as any).colors,
        dvFontSizeRange: (spec as any).dvFontSizeRange,
        dvFontWeightRange: (spec as any).dvFontWeightRange,
        ranking: (spec as any).ranking,
        customClickEvent: (spec as any).customClickEvent,
        customHoverEvent: (spec as any).customHoverEvent
      }
    ],
    drillableTreemap: [
      {
        type: 'drillableTreemap',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute
        }
      }
    ],
    markerLine: [
      {
        type: 'markerLine',
        encoding: {
          x: (spec as any).xAxisAttribute,
          y: (spec as any).yAxisAttribute,
          y2: (spec as any).y2,
          subTitle: (spec as any).subTitle,
          desc: (spec as any).desc,
          link: (spec as any).link
        }
      }
    ]
  };
  /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any */

  converted.view = {
    main: {
      layers:
        map[
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          (spec as unknown as { type: string }).type
        ]
    }
  };
  const layerDefine = getLayer(converted.view.main.layers[0].type);
  converted.view = {
    ...converted.view,
    secondary:
      typeof (spec as unknown as { stocks: [] }).stocks !== 'undefined' &&
      typeof spec.data !== 'undefined' &&
      layerDefine.isStandardChartLayer
        ? {
            layers: [
              {
                type: 'simpleKLine',
                stocks: (spec as unknown as { stocks: [] }).stocks
              }
            ]
          }
        : undefined
  };

  return converted;
}
